import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Stack,
  IconButton,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  Grid,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Star as StarIcon,
  Visibility as VisibilityIcon,
  AccountBalance as AccountBalanceIcon,
  Error as ErrorIcon,
  Shield as ShieldIcon,
} from '@mui/icons-material';
import { riderService, RiderBankDetails } from '../../services';
import BankDetailsDialog from './dialogs/BankDetailsDialog';

interface BankDetailsSectionProps {
  riderId: string;
}

const BankDetailsSection: React.FC<BankDetailsSectionProps> = ({ riderId }) => {
  const [loading, setLoading] = useState(false);
  const [bankDetails, setBankDetails] = useState<RiderBankDetails[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedBank, setSelectedBank] = useState<RiderBankDetails | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; bankId: string | null }>({
    open: false,
    bankId: null,
  });
  const [verifyDialog, setVerifyDialog] = useState<{
    open: boolean;
    bankId: string | null;
    action: 'verify' | 'reject' | null;
  }>({
    open: false,
    bankId: null,
    action: null,
  });
  const [verificationNotes, setVerificationNotes] = useState('');
  const [documentPreview, setDocumentPreview] = useState<{ open: boolean; url: string | null }>({
    open: false,
    url: null,
  });
  const [surepassDialog, setSurepassDialog] = useState<{ open: boolean; bankId: string | null; loading: boolean; result: any | null; error: string | null }>({
    open: false,
    bankId: null,
    loading: false,
    result: null,
    error: null,
  });
  const [viewDetailsDialog, setViewDetailsDialog] = useState<{ open: boolean; bankDetails: RiderBankDetails | null }>({
    open: false,
    bankDetails: null,
  });

  useEffect(() => {
    if (riderId) {
      loadBankDetails();
    }
  }, [riderId]);

  // Auto-refresh presigned URLs every 50 minutes
  useEffect(() => {
    const refreshInterval = setInterval(() => {
      console.log('[BankDetailsSection] Auto-refreshing bank proof presigned URLs (50min interval)');
      loadBankDetails();
    }, 50 * 60 * 1000); // 50 minutes

    return () => clearInterval(refreshInterval);
  }, [riderId]);

  const loadBankDetails = async () => {
    console.log('[BankDetailsSection] Loading bank details for riderId:', riderId);
    setLoading(true);
    setError(null);
    try {
      const response = await riderService.getRiderBankDetails(riderId);
      console.log('[BankDetailsSection] API response:', response);
      if (response.success) {
        setBankDetails(response.data || []);
        console.log('[BankDetailsSection] Set bank details:', response.data?.length, 'accounts');
      }
    } catch (err: any) {
      console.error('[BankDetailsSection] Error loading bank details:', err);
      setError('Failed to load bank details');
    } finally {
      setLoading(false);
      console.log('[BankDetailsSection] Loading complete');
    }
  };

  // Fetch fresh presigned URL before viewing bank proof document
  const handleViewBankProof = useCallback(async (bankId: string, currentUrl: string | null) => {
    if (!currentUrl) {
      console.warn('[BankDetailsSection] No proof document URL available');
      return;
    }

    try {
      console.log(`[BankDetailsSection] Fetching fresh presigned URL for bank ${bankId}`);

      // Fetch fresh bank details to get updated presigned URL
      const response = await riderService.getRiderBankDetails(riderId);

      if (response.success && response.data) {
        // Find the bank with matching ID
        const freshBank = response.data.find(bank => bank.id === bankId);

        if (freshBank && freshBank.proofDocumentUrl) {
          console.log(`[BankDetailsSection] Using fresh presigned URL for bank proof`);
          setDocumentPreview({ open: true, url: freshBank.proofDocumentUrl });

          // Update local state with fresh data
          setBankDetails(response.data);
        } else {
          console.warn(`[BankDetailsSection] No fresh URL found, using current URL`);
          setDocumentPreview({ open: true, url: currentUrl });
        }
      } else {
        // Fallback to current URL if fetch fails
        console.warn('[BankDetailsSection] Failed to fetch fresh URLs, using current URL');
        setDocumentPreview({ open: true, url: currentUrl });
      }
    } catch (error) {
      console.error('[BankDetailsSection] Error fetching fresh presigned URL:', error);
      // Fallback to current URL on error
      setDocumentPreview({ open: true, url: currentUrl });
    }
  }, [riderId]);

  const handleAddNew = () => {
    setSelectedBank(null);
    setDialogOpen(true);
  };

  const handleEdit = (bank: RiderBankDetails) => {
    setSelectedBank(bank);
    setDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteDialog.bankId) return;

    try {
      await riderService.deleteBankDetails(deleteDialog.bankId);
      setDeleteDialog({ open: false, bankId: null });
      loadBankDetails();
    } catch (err: any) {
      console.error('Error deleting bank details:', err);
      setError('Failed to delete bank details');
    }
  };

  const handleSetPrimary = async (bankId: string) => {
    try {
      await riderService.setPrimaryAccount(bankId);
      loadBankDetails();
    } catch (err: any) {
      console.error('Error setting primary account:', err);
      setError('Failed to set primary account');
    }
  };

  const handleVerify = async () => {
    if (!verifyDialog.bankId || !verifyDialog.action) return;

    try {
      if (verifyDialog.action === 'verify') {
        await riderService.verifyBankDetails(verifyDialog.bankId, verificationNotes);
      } else {
        if (!verificationNotes) {
          setError('Verification notes are required for rejection');
          return;
        }
        await riderService.rejectBankDetails(verifyDialog.bankId, verificationNotes);
      }
      setVerifyDialog({ open: false, bankId: null, action: null });
      setVerificationNotes('');
      loadBankDetails();
    } catch (err: any) {
      console.error('Error verifying bank details:', err);
      setError('Failed to update verification status');
    }
  };

  const handleSurepassVerification = async (bankId: string) => {
    console.log(`[BankDetailsSection] Starting Surepass verification for bank ${bankId}`);
    setSurepassDialog({ open: true, bankId, loading: true, result: null, error: null });

    try {
      const response = await riderService.verifyBankDetailsUsingSurepass(bankId);
      console.log(`[BankDetailsSection] Surepass verification response:`, response);

      if (response.success) {
        setSurepassDialog(prev => ({
          ...prev,
          loading: false,
          result: response.data,
          error: null,
        }));
        // Refresh bank details after successful verification
        await new Promise(resolve => setTimeout(resolve, 1500));
        loadBankDetails();
      } else {
        setSurepassDialog(prev => ({
          ...prev,
          loading: false,
          error: response.message || 'Verification failed',
        }));
      }
    } catch (err: any) {
      console.error(`[BankDetailsSection] Error during Surepass verification:`, err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to verify bank details';
      setSurepassDialog(prev => ({
        ...prev,
        loading: false,
        error: errorMessage,
      }));
    }
  };

  const handleViewDetails = (bank: RiderBankDetails) => {
    setViewDetailsDialog({ open: true, bankDetails: bank });
  };

  const getBackendStatusChipProps = (status?: string | null) => {
    const mapping: Record<string, { label: string; color: 'success' | 'warning' | 'error' | 'default' }> = {
      success: { label: 'Success', color: 'success' },
      invalid_ifsc: { label: 'Invalid IFSC', color: 'error' },
      invalid_account: { label: 'Invalid Account', color: 'error' },
      nre_account: { label: 'NRE Account', color: 'warning' },
      account_closed: { label: 'Account Closed', color: 'error' },
      account_blocked_frozen: { label: 'Blocked / Frozen', color: 'error' },
      imps_not_enabled: { label: 'IMPS Not Enabled', color: 'warning' },
      backend_down: { label: 'Backend Down', color: 'warning' },
      transaction_failed: { label: 'Transaction Failed', color: 'error' },
    };

    return mapping[status || ''] || {
      label: status ? status.replace(/_/g, ' ').toUpperCase() : 'UNKNOWN',
      color: 'default',
    };
  };

  const getVerificationColor = (status: string) => {
    switch (status) {
      case 'verified':
        return 'success';
      case 'rejected':
        return 'error';
      default:
        return 'warning';
    }
  };

  const renderBackendStatusChip = (status?: string | null) => {
    const chip = getBackendStatusChipProps(status);
    return (
      <Chip
        label={chip.label}
        color={chip.color}
        size="small"
        variant={chip.color === 'default' ? 'outlined' : 'filled'}
      />
    );
  };

  const normalizeAccountExistsValue = (value?: boolean | string | null): 'yes' | 'no' | null => {
    if (value === true || value === 'yes' || value === 'YES') return 'yes';
    if (value === false || value === 'no' || value === 'NO') return 'no';
    return null;
  };

  const renderAccountExistsBadge = (value?: boolean | string | null) => {
    const normalized = normalizeAccountExistsValue(value);

    if (normalized === 'yes') {
      return (
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            px: 1.5,
            py: 0.75,
            borderRadius: '20px',
            background: 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)',
            boxShadow: '0 4px 12px rgba(76, 175, 80, 0.25)',
            border: '2px solid #2E7D32',
          }}
        >
          <CheckCircleIcon sx={{ fontSize: '1rem', color: '#fff' }} />
          <Typography variant="body2" fontWeight="bold" sx={{ color: '#fff', textTransform: 'uppercase', fontSize: '0.75rem' }}>
            Yes
          </Typography>
        </Box>
      );
    }

    if (normalized === 'no') {
      return (
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            px: 1.5,
            py: 0.75,
            borderRadius: '20px',
            background: 'linear-gradient(135deg, #F44336 0%, #E53935 100%)',
            boxShadow: '0 4px 12px rgba(244, 67, 54, 0.25)',
            border: '2px solid #C62828',
          }}
        >
          <ErrorIcon sx={{ fontSize: '1rem', color: '#fff' }} />
          <Typography variant="body2" fontWeight="bold" sx={{ color: '#fff', textTransform: 'uppercase', fontSize: '0.75rem' }}>
            No
          </Typography>
        </Box>
      );
    }

    return <Chip label="N/A" size="small" variant="outlined" />;
  };

  const maskAccountNumber = (accountNumber: string) => {
    if (!accountNumber) return '';
    if (accountNumber.length <= 4) return accountNumber;
    return `XXXX${accountNumber.slice(-4)}`;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  console.log('[BankDetailsSection] RENDERING - bankDetails count:', bankDetails.length, 'loading:', loading);

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6" display="flex" alignItems="center" gap={1}>
          <AccountBalanceIcon /> Bank Account Details ⭐ TAB VIEW
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddNew}>
          Add Bank Account
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {bankDetails.length === 0 ? (
        <Card>
          <CardContent>
            <Box textAlign="center" py={4}>
              <AccountBalanceIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
              <Typography variant="body1" color="text.secondary" gutterBottom>
                No bank account details added yet
              </Typography>
              <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddNew} sx={{ mt: 2 }}>
                Add First Bank Account
              </Button>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Account Holder</TableCell>
                <TableCell>Bank Name</TableCell>
                <TableCell>Account Number</TableCell>
                <TableCell>IFSC Code</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Primary Status</TableCell>
                <TableCell>Account Exists</TableCell>
                <TableCell>Backend Status</TableCell>
                <TableCell>Proof</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {bankDetails.map((bank) => (
                <TableRow
                  key={bank.id}
                  onClick={() => handleViewDetails(bank)}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                  }}
                >
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {bank.accountHolderName}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{bank.bankName}</Typography>
                    {bank.branchName && (
                      <Typography variant="caption" color="text.secondary">
                        {bank.branchName}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontFamily="monospace">
                      {maskAccountNumber(bank.accountNumber)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontFamily="monospace">
                      {bank.ifscCode}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={bank.accountType} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    {/* Primary Status Badge */}
                    {bank.isPrimary ? (
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.5,
                          px: 1.5,
                          py: 0.75,
                          borderRadius: '20px',
                          background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                          boxShadow: '0 4px 12px rgba(255, 215, 0, 0.3)',
                          border: '2px solid #FF8C00',
                        }}
                      >
                        <StarIcon sx={{ fontSize: '1rem', color: '#fff' }} />
                        <Typography variant="body2" fontWeight="bold" sx={{ color: '#fff', textTransform: 'uppercase', fontSize: '0.75rem' }}>
                          Primary
                        </Typography>
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.5,
                          px: 1.5,
                          py: 0.75,
                          borderRadius: '20px',
                          background: 'linear-gradient(135deg, #E0E0E0 0%, #BDBDBD 100%)',
                          border: '1px solid #9E9E9E',
                        }}
                      >
                        <Typography variant="body2" sx={{ color: '#666', textTransform: 'uppercase', fontSize: '0.75rem', fontWeight: '500' }}>
                          Secondary
                        </Typography>
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>
                    {/* Account Exists Status */}
                    {renderAccountExistsBadge(bank.account_exists)}
                  </TableCell>
                  <TableCell>
                    {renderBackendStatusChip(bank.status)}
                  </TableCell>
                  <TableCell>
                    {bank.proofDocumentUrl ? (
                      <Tooltip title="View Bank Proof">
                        <IconButton
                          size="small"
                          onClick={() => handleViewBankProof(bank.id, bank.proofDocumentUrl)}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        No proof
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={1} justifyContent="center">
                      <Tooltip title={normalizeAccountExistsValue(bank.account_exists) === 'yes' && bank.status === 'success' ? "Edit disabled - Account verified" : "Edit"}>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(bank)}
                            color="primary"
                            disabled={normalizeAccountExistsValue(bank.account_exists) === 'yes' && bank.status === 'success'}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>

                      {!bank.isPrimary && bank.isActive && (
                        <Tooltip title="Set as Primary">
                          <IconButton size="small" onClick={() => handleSetPrimary(bank.id)} color="warning">
                            <StarIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}

                      {bank.verificationStatus === 'pending' && (
                        <>
                          {/* Surepass Verification Button - Large and Prominent */}
                          <Tooltip title="Verify with Surepass API" arrow>
                            <Button
                              size="small"
                              onClick={() => handleSurepassVerification(bank.id)}
                              sx={{
                                background: 'linear-gradient(135deg, #1976d2 0%, #0d47a1 100%)',
                                color: '#fff',
                                textTransform: 'uppercase',
                                fontWeight: 'bold',
                                fontSize: '0.7rem',
                                padding: '6px 12px',
                                borderRadius: '20px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                border: '2px solid #0d47a1',
                                boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                  background: 'linear-gradient(135deg, #42a5f5 0%, #1976d2 100%)',
                                  boxShadow: '0 6px 16px rgba(25, 118, 210, 0.5)',
                                  transform: 'scale(1.05)',
                                },
                              }}
                              startIcon={<ShieldIcon sx={{ fontSize: '1rem' }} />}
                            >
                              Click here to Verify Bank Details
                            </Button>
                          </Tooltip>
                
                        </>
                      )}

                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => setDeleteDialog({ open: true, bankId: bank.id })}
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add/Edit Bank Details Dialog */}
      <BankDetailsDialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setSelectedBank(null);
        }}
        riderId={riderId}
        bankDetails={selectedBank}
        onSuccess={loadBankDetails}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, bankId: null })}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this bank account? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, bankId: null })}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Verification Dialog */}
      <Dialog
        open={verifyDialog.open}
        onClose={() => {
          setVerifyDialog({ open: false, bankId: null, action: null });
          setVerificationNotes('');
        }}
      >
        <DialogTitle>
          {verifyDialog.action === 'verify' ? 'Verify Bank Details' : 'Reject Bank Details'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            label={verifyDialog.action === 'reject' ? 'Rejection Reason (Required)' : 'Notes (Optional)'}
            value={verificationNotes}
            onChange={(e) => setVerificationNotes(e.target.value)}
            sx={{ mt: 2 }}
            required={verifyDialog.action === 'reject'}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setVerifyDialog({ open: false, bankId: null, action: null });
              setVerificationNotes('');
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleVerify}
            color={verifyDialog.action === 'verify' ? 'success' : 'error'}
            variant="contained"
          >
            {verifyDialog.action === 'verify' ? 'Verify' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Document Preview Dialog */}
      <Dialog
        open={documentPreview.open}
        onClose={() => setDocumentPreview({ open: false, url: null })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Bank Proof Document
          <IconButton
            aria-label="close"
            onClick={() => setDocumentPreview({ open: false, url: null })}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CancelIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {documentPreview.url && (
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                p: 2,
                gap: 2,
              }}
            >
              {documentPreview.url.toLowerCase().includes('.pdf') ? (
                // PDF viewer - Show message and open button
                <Box
                  sx={{
                    textAlign: 'center',
                    p: 4,
                    border: '2px dashed #ddd',
                    borderRadius: 2,
                    width: '100%',
                    bgcolor: '#f9f9f9',
                  }}
                >
                  <AccountBalanceIcon sx={{ fontSize: 80, color: 'primary.main', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    PDF Document Ready
                  </Typography>
                  <Typography variant="body2" color="textSecondary" paragraph>
                    Click the button below to view the PDF document in a new tab
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    startIcon={<VisibilityIcon />}
                    onClick={() => window.open(documentPreview.url!, '_blank')}
                    sx={{ mt: 2 }}
                  >
                    Open PDF in New Tab
                  </Button>
                </Box>
              ) : (
                // Image viewer - Display inline with error handling
                <Box
                  component="img"
                  src={documentPreview.url}
                  alt="Bank Proof Document"
                  sx={{
                    maxWidth: '100%',
                    maxHeight: '70vh',
                    objectFit: 'contain',
                    borderRadius: 1,
                    boxShadow: 2,
                  }}
                  onError={(e) => {
                    console.error('[BankDetailsSection] Error loading image:', e);
                  }}
                />
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDocumentPreview({ open: false, url: null })}>
            Close
          </Button>
          {documentPreview.url && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<VisibilityIcon />}
              onClick={() => window.open(documentPreview.url!, '_blank')}
            >
              Open Full Size
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Surepass Verification Dialog */}
      <Dialog
        open={surepassDialog.open}
        onClose={() => setSurepassDialog({ open: false, bankId: null, loading: false, result: null, error: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box display="flex" alignItems="center" gap={1}>
            <AccountBalanceIcon color="primary" />
            <Typography variant="h6">Surepass Bank Verification</Typography>
          </Box>
          <IconButton
            aria-label="close"
            onClick={() => setSurepassDialog({ open: false, bankId: null, loading: false, result: null, error: null })}
            sx={{ color: 'grey.500' }}
          >
            <CancelIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ py: 3 }}>
          {surepassDialog.loading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 4 }}>
              <CircularProgress />
              <Typography color="textSecondary" align="center">
                Verifying bank account details with Surepass...
              </Typography>
            </Box>
          ) : surepassDialog.error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight="bold" gutterBottom>
                Verification Failed
              </Typography>
              <Typography variant="body2">{surepassDialog.error}</Typography>
            </Alert>
          ) : surepassDialog.result ? (
            <Stack spacing={2}>
              {/* Result Header */}
              <Alert
                severity={surepassDialog.result.status === 'success' ? 'success' : 'warning'}
                icon={surepassDialog.result.status === 'success' ? <CheckCircleIcon /> : <ErrorIcon />}
              >
                <Typography variant="body2" fontWeight="bold">
                  Surepass Verification Result
                </Typography>
                <Typography variant="body2">
                  {getBackendStatusChipProps(surepassDialog.result.status).label}
                </Typography>
              </Alert>

              {/* Account Existence Status */}
              <Card
                variant="outlined"
                sx={{
                  bgcolor:
                    normalizeAccountExistsValue(surepassDialog.result.account_exists) === 'yes'
                      ? '#f0f8f0'
                      : normalizeAccountExistsValue(surepassDialog.result.account_exists) === 'no'
                      ? '#fff3f3'
                      : '#f5f5f5',
                  borderColor:
                    normalizeAccountExistsValue(surepassDialog.result.account_exists) === 'yes'
                      ? '#4caf50'
                      : normalizeAccountExistsValue(surepassDialog.result.account_exists) === 'no'
                      ? '#f44336'
                      : '#bdbdbd',
                }}
              >
                <CardContent>
                  <Typography variant="caption" color="textSecondary" sx={{ textTransform: 'uppercase', fontWeight: 'bold' }}>
                    Account Existence
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1} mt={1}>
                    {surepassDialog.result.account_exists === 'yes' ? (
                      <>
                        <CheckCircleIcon sx={{ color: '#4caf50', fontSize: '1.5rem' }} />
                        <Typography variant="body2" fontWeight="bold" color="success.main">
                          Account Exists
                        </Typography>
                      </>
                    ) : surepassDialog.result.account_exists === 'no' ? (
                      <>
                        <CancelIcon sx={{ color: '#f44336', fontSize: '1.5rem' }} />
                        <Typography variant="body2" fontWeight="bold" color="error">
                          Account Does Not Exist
                        </Typography>
                      </>
                    ) : (
                      <>
                        <ErrorIcon sx={{ color: '#757575', fontSize: '1.5rem' }} />
                        <Typography variant="body2" fontWeight="bold" color="text.secondary">
                          Unknown
                        </Typography>
                      </>
                    )}
                  </Box>
                </CardContent>
              </Card>

              {/* Account Holder Name */}
              <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="caption" color="textSecondary" sx={{ textTransform: 'uppercase', fontWeight: 'bold' }}>Account Holder Name</Typography>
                <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 500 }}>
                  {surepassDialog.result.full_name || 'N/A'}
                </Typography>
              </Box>

              {/* IFSC Details */}
              {surepassDialog.result.ifsc_details && (
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="caption" color="textSecondary" sx={{ textTransform: 'uppercase', fontWeight: 'bold', display: 'block', mb: 1 }}>
                      Bank Details (from IFSC)
                    </Typography>
                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="textSecondary">Bank Name</Typography>
                        <Typography variant="body2" fontWeight="500">
                          {surepassDialog.result.ifsc_details.bank_name || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="textSecondary">Branch</Typography>
                        <Typography variant="body2" fontWeight="500">
                          {surepassDialog.result.ifsc_details.branch || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="caption" color="textSecondary">Address</Typography>
                        <Typography variant="body2" fontWeight="500">
                          {surepassDialog.result.ifsc_details.address || 'N/A'}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              )}

              {/* Status & Remarks */}
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                  <Typography variant="caption" color="textSecondary" sx={{ textTransform: 'uppercase', fontWeight: 'bold' }}>
                    Backend Status
                  </Typography>
                  <Chip
                    label={getBackendStatusChipProps(surepassDialog.result.status).label}
                    color={getBackendStatusChipProps(surepassDialog.result.status).color}
                    size="small"
                    sx={{ mt: 1 }}
                    variant={getBackendStatusChipProps(surepassDialog.result.status).color === 'default' ? 'outlined' : 'filled'}
                  />
                </Box>
                <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                  <Typography variant="caption" color="textSecondary" sx={{ textTransform: 'uppercase', fontWeight: 'bold' }}>
                    Client ID
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 500, wordBreak: 'break-all' }}>
                    {surepassDialog.result.client_id || 'N/A'}
                  </Typography>
                </Box>
              </Box>

              {surepassDialog.result.remarks && (
                <Box sx={{ p: 2, bgcolor: '#fff9e6', border: '1px solid #ffe082', borderRadius: 1 }}>
                  <Typography variant="caption" color="textSecondary" sx={{ textTransform: 'uppercase', fontWeight: 'bold' }}>Remarks</Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {surepassDialog.result.remarks}
                  </Typography>
                </Box>
              )}
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={() => setSurepassDialog({ open: false, bankId: null, loading: false, result: null, error: null })}
            variant="outlined"
          >
            Close
          </Button>
          {surepassDialog.result && surepassDialog.result.account_exists === 'yes' && (
            <Button
              onClick={() => {
                // Auto-verify the bank if account exists
                if (surepassDialog.bankId) {
                  handleVerify();
                  setSurepassDialog({ open: false, bankId: null, loading: false, result: null, error: null });
                }
              }}
              variant="contained"
              color="success"
              startIcon={<CheckCircleIcon />}
            >
              Mark as Verified
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* View Details Dialog */}
      <Dialog
        open={viewDetailsDialog.open}
        onClose={() => setViewDetailsDialog({ open: false, bankDetails: null })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box display="flex" alignItems="center" gap={1}>
            <AccountBalanceIcon color="primary" />
            <Typography variant="h6">Bank Account Details</Typography>
          </Box>
          <IconButton
            aria-label="close"
            onClick={() => setViewDetailsDialog({ open: false, bankDetails: null })}
            sx={{ color: 'grey.500' }}
          >
            <CancelIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ py: 3 }}>
          {viewDetailsDialog.bankDetails && (
            <Stack spacing={3}>
              {/* Basic Information */}
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom color="primary">
                    Basic Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="textSecondary">Account Holder Name</Typography>
                      <Typography variant="body1" fontWeight="500">
                        {viewDetailsDialog.bankDetails.accountHolderName}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="textSecondary">Account Type</Typography>
                      <Typography variant="body1" fontWeight="500">
                        {viewDetailsDialog.bankDetails.accountType}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="textSecondary">Account Number</Typography>
                      <Typography variant="body1" fontFamily="monospace" fontWeight="500">
                        {viewDetailsDialog.bankDetails.accountNumber}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="textSecondary">IFSC Code</Typography>
                      <Typography variant="body1" fontFamily="monospace" fontWeight="500">
                        {viewDetailsDialog.bankDetails.ifscCode}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Bank Information */}
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom color="primary">
                    Bank Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="textSecondary">Bank Name</Typography>
                      <Typography variant="body1" fontWeight="500">
                        {viewDetailsDialog.bankDetails.bankName}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="textSecondary">Branch Name</Typography>
                      <Typography variant="body1" fontWeight="500">
                        {viewDetailsDialog.bankDetails.branchName || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="textSecondary">Branch Address</Typography>
                      <Typography variant="body1" fontWeight="500">
                        {viewDetailsDialog.bankDetails.branchAddress || 'N/A'}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Status Information */}
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom color="primary">
                    Status Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="textSecondary">Primary Status</Typography>
                      <Box mt={1}>
                        {viewDetailsDialog.bankDetails.isPrimary ? (
                          <Box
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.5,
                              px: 1.5,
                              py: 0.75,
                              borderRadius: '20px',
                              background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                              boxShadow: '0 4px 12px rgba(255, 215, 0, 0.3)',
                              border: '2px solid #FF8C00',
                            }}
                          >
                            <StarIcon sx={{ fontSize: '1rem', color: '#fff' }} />
                            <Typography variant="body2" fontWeight="bold" sx={{ color: '#fff', textTransform: 'uppercase', fontSize: '0.75rem' }}>
                              Primary
                            </Typography>
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.5,
                              px: 1.5,
                              py: 0.75,
                              borderRadius: '20px',
                              background: 'linear-gradient(135deg, #E0E0E0 0%, #BDBDBD 100%)',
                              border: '1px solid #9E9E9E',
                            }}
                          >
                            <Typography variant="body2" sx={{ color: '#666', textTransform: 'uppercase', fontSize: '0.75rem', fontWeight: '500' }}>
                              Secondary
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="textSecondary">Account Exists</Typography>
                      <Box mt={1}>
                        {renderAccountExistsBadge(viewDetailsDialog.bankDetails.account_exists)}
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="textSecondary">Backend Status</Typography>
                      <Box mt={1}>
                        {renderBackendStatusChip(viewDetailsDialog.bankDetails.status)}
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="textSecondary">Verification Status</Typography>
                      <Box mt={1}>
                        <Chip
                          label={viewDetailsDialog.bankDetails.verificationStatus.toUpperCase()}
                          color={getVerificationColor(viewDetailsDialog.bankDetails.verificationStatus)}
                          size="small"
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Additional Information */}
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom color="primary">
                    Additional Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="textSecondary">Proof Document Type</Typography>
                      <Typography variant="body1" fontWeight="500">
                        {viewDetailsDialog.bankDetails.proofDocumentType || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="textSecondary">Active Status</Typography>
                      <Typography variant="body1" fontWeight="500">
                        {viewDetailsDialog.bankDetails.isActive ? 'Active' : 'Inactive'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="textSecondary">Notes</Typography>
                      <Typography variant="body1" fontWeight="500">
                        {viewDetailsDialog.bankDetails.notes || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="textSecondary">Verification Notes</Typography>
                      <Typography variant="body1" fontWeight="500">
                        {viewDetailsDialog.bankDetails.verificationNotes || 'N/A'}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Surepass Information */}
              {(viewDetailsDialog.bankDetails.status || viewDetailsDialog.bankDetails.account_exists !== undefined) && (
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom color="primary">
                      Surepass Verification Details
                    </Typography>
                    <Grid container spacing={2}>
                      {viewDetailsDialog.bankDetails.client_id && (
                        <Grid item xs={12} sm={6}>
                          <Typography variant="caption" color="textSecondary">Client ID</Typography>
                          <Typography variant="body1" fontFamily="monospace" fontWeight="500">
                            {viewDetailsDialog.bankDetails.client_id}
                          </Typography>
                        </Grid>
                      )}
                      {viewDetailsDialog.bankDetails.full_name && (
                        <Grid item xs={12} sm={6}>
                          <Typography variant="caption" color="textSecondary">Full Name (from Surepass)</Typography>
                          <Typography variant="body1" fontWeight="500">
                            {viewDetailsDialog.bankDetails.full_name}
                          </Typography>
                        </Grid>
                      )}
                      {viewDetailsDialog.bankDetails.remarks && (
                        <Grid item xs={12}>
                          <Typography variant="caption" color="textSecondary">Remarks</Typography>
                          <Typography variant="body1" fontWeight="500">
                            {viewDetailsDialog.bankDetails.remarks}
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </CardContent>
                </Card>
              )}

              {/* Timestamps */}
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom color="primary">
                    Timestamps
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="textSecondary">Created At</Typography>
                      <Typography variant="body1" fontWeight="500">
                        {new Date(viewDetailsDialog.bankDetails.createdAt).toLocaleString()}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="textSecondary">Last Updated</Typography>
                      <Typography variant="body1" fontWeight="500">
                        {new Date(viewDetailsDialog.bankDetails.updatedAt).toLocaleString()}
                      </Typography>
                    </Grid>
                    {viewDetailsDialog.bankDetails.verificationDate && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="textSecondary">Verification Date</Typography>
                        <Typography variant="body1" fontWeight="500">
                          {new Date(viewDetailsDialog.bankDetails.verificationDate).toLocaleString()}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={() => setViewDetailsDialog({ open: false, bankDetails: null })}
            variant="outlined"
          >
            Close
          </Button>
          {viewDetailsDialog.bankDetails?.proofDocumentUrl && (
            <Button
              onClick={() => handleViewBankProof(viewDetailsDialog.bankDetails!.id, viewDetailsDialog.bankDetails!.proofDocumentUrl)}
              variant="contained"
              color="primary"
              startIcon={<VisibilityIcon />}
            >
              View Proof Document
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BankDetailsSection;
